<template>
  <div class="bch-account">
    <!-- 数字资产资金管理终端背景 -->
    <div class="account-grid-layer"></div>
    <div class="account-scan-beam"></div>
    <div class="account-aurora account-aurora-cyan"></div>
    <div class="account-aurora account-aurora-emerald"></div>
    <div class="account-particles" aria-hidden="true">
      <i></i>
      <i></i>
      <i></i>
      <i></i>
      <i></i>
    </div>

    <AppHeader />

    <main class="page-body">
      <!-- ================= 顶部：首页同款风格 ================= -->
      <section class="hero-premium">
        <div class="hero-grid-bg"></div>
        <div class="hero-glow-gold"></div>
        <div class="hero-glow-blue"></div>
        <div class="hero-scan-line"></div>

        <div class="hero-data-stream" aria-hidden="true">
          <span></span>
          <span></span>
          <span></span>
        </div>

        <div class="arbitrage-orbit">
          <div class="orbit-ring ring-a"></div>
          <div class="orbit-ring ring-b"></div>
          <div class="orbit-ring ring-c"></div>

          <div class="orbit-core">B</div>

          <div class="orbit-dot dot-a"></div>
          <div class="orbit-dot dot-b"></div>
        </div>

        <div class="hero-content">
          <div class="hero-badge">
            <i></i>
            {{ tr("bchAccount.treasury.hero.badge", "数字资产资金管理系统") }}
          </div>

          <h1>
            <span class="hero-title-main">
              {{ tr("bchAccount.treasury.hero.titleMain", "数字资产") }}
            </span>
            <span class="hero-title-accent">
              {{ tr("bchAccount.treasury.hero.titleAccent", "资金管理中心") }}
            </span>
          </h1>

          <p class="hero-slogan">
            {{
              tr(
                "bchAccount.treasury.hero.slogan",
                "可用流动性 · 策略结算 · 资产划转 · 实时分类账"
              )
            }}
          </p>

          <p class="hero-desc">
            {{
              tr(
                "bchAccount.treasury.hero.desc",
                "集中管理可用结算余额、待处理资产与资金变动记录，为数字资产策略提供透明、可审计的账户基础设施。"
              )
            }}
          </p>

          <div class="hero-capability-grid">
            <div>
              <small>TREASURY</small>
              <strong>资产资金管理</strong>
            </div>
            <div>
              <small>SETTLEMENT</small>
              <strong>实时清算分类账</strong>
            </div>
            <div>
              <small>ON-CHAIN</small>
              <strong>链上资产划转</strong>
            </div>
          </div>
        </div>
      </section>

      <!-- ================= 账户余额＋资产划转 ================= -->
      <section
        class="balance-card"
        :class="{ 'is-disconnected': !walletConnected }"
      >
        <div class="balance-main">
          <div class="balance-kicker">
            <i></i>
            TREASURY ACCOUNT
          </div>

          <div class="balance-head">
            <span class="balance-label">
              {{ tr("bchAccount.treasury.balance.available", "可用结算余额") }}
            </span>

            <span
              class="wallet-state"
              :class="walletConnected ? 'connected' : 'disconnected'"
            >
              <i></i>
              {{
                walletConnected
                  ? tr("bchAccount.wallet.connected", "钱包已连接")
                  : tr("bchAccount.wallet.disconnected", "钱包未连接")
              }}
            </span>
          </div>

          <div class="balance-value">
            <strong>
              {{
                overviewLoading
                  ? "--"
                  : money(availableBalance, 2)
              }}
            </strong>
            <span>{{ coinType }}</span>
          </div>

          <div class="balance-meta">
            <div class="balance-meta-item">
              <span>
                {{ tr("bchAccount.treasury.balance.processing", "资产划转处理中") }}
              </span>
              <strong>
                {{ money(frozenAmount, 2) }} {{ coinType }}
              </strong>
            </div>

            <div class="balance-meta-item">
              <span>
                {{ tr("bchAccount.treasury.balance.pending", "待确认结算") }}
              </span>
              <strong>
                {{ money(pendingAmount, 2) }} {{ coinType }}
              </strong>
            </div>

            <div class="balance-meta-item total-equity">
              <span>
                {{ tr("bchAccount.treasury.balance.total", "账户总额") }}
              </span>
              <strong>
                {{ money(totalCashAmount, 2) }} {{ coinType }}
              </strong>
            </div>
          </div>
        </div>

        <button
          type="button"
          class="withdraw-button"
          :disabled="withdrawDisabled"
          @click="openWithdraw"
        >
          {{ withdrawButtonText }}
        </button>
      </section>

      <!-- ================= 账户记录 ================= -->
      <section class="ledger-card">
        <div class="ledger-header">
          <div>
            <h2>
              {{ tr("bchAccount.treasury.ledger.title", "资产分类账") }}
            </h2>
            <p>
              {{
                tr(
                  "bchAccount.treasury.ledger.desc",
                  "策略结算、协作结算与资产划转的完整资金记录"
                )
              }}
            </p>
          </div>

          <span class="ledger-badge">ASSET LEDGER</span>
        </div>

        <!-- 当前分类统计 -->
        <div
          v-if="walletConnected && !listLoading"
          class="ledger-summary"
        >
          <div>
            <span>
              {{ tr("bchAccount.treasury.summary.inflow", "已结算流入") }}
            </span>
            <strong class="summary-income">
              +{{ money(summary.successIncomeAmount, 2) }}
            </strong>
          </div>

          <div>
            <span>
              {{ tr("bchAccount.treasury.summary.outflow", "已结算流出") }}
            </span>
            <strong class="summary-expense">
              -{{ money(summary.successExpenseAmount, 2) }}
            </strong>
          </div>

          <div>
            <span>
              {{ tr("bchAccount.treasury.summary.net", "净资金变动") }}
            </span>
            <strong :class="netAmountClass">
              {{ signedMoney(summary.successNetAmount, 2) }}
            </strong>
          </div>
        </div>

        <!-- 分类条件 -->
        <div class="ledger-tabs">
          <button
            v-for="tab in tabs"
            :key="tab.value"
            type="button"
            :class="{ active: activeTab === tab.value }"
            :disabled="!walletConnected || listLoading"
            @click="changeTab(tab.value)"
          >
            {{ tr(tab.labelKey, tab.fallback) }}
          </button>
        </div>

        <!-- 加载状态 -->
        <div v-if="walletConnected && listLoading" class="ledger-loading">
          <span class="loading-spinner"></span>
          <p>
            {{ tr("bchAccount.treasury.message.loading", "正在同步资产分类账") }}
          </p>
        </div>

        <!-- 明细列表 -->
        <div
          v-else-if="walletConnected && list.length"
          class="ledger-list"
        >
          <article
            v-for="(item, index) in list"
            :key="item.id || item.serialNo || index"
            class="ledger-item"
          >
            <div class="record-left">
              <div class="record-title-row">
                <strong>{{ recordTitle(item) }}</strong>

                <span
                  v-if="item.state"
                  class="record-state"
                  :class="'state-' + item.state"
                >
                  {{ recordState(item) }}
                </span>
              </div>

              <span>{{ recordDescription(item) }}</span>
            </div>

            <div class="record-right">
              <strong
                class="record-amount"
                :class="item.amountStyle || directionStyle(item)"
              >
                {{ item.amountSign || directionSign(item) }}
                {{ money(item.amount, 4) }}
                <small>{{ item.coinType || coinType }}</small>
              </strong>

              <time>{{ formatTime(item.createTime) }}</time>
            </div>
          </article>
        </div>

        <!-- 未连接钱包 -->
        <div v-else-if="!walletConnected" class="empty-record wallet-empty">
          <strong>
            {{ tr("bchAccount.treasury.wallet.connectTitle", "请先连接数字钱包") }}
          </strong>
          <span>
            {{
              tr(
                "bchAccount.treasury.wallet.connectDesc",
                "连接钱包后可读取当前资产余额、清算状态与资金分类账。"
              )
            }}
          </span>
        </div>

        <!-- 空记录 -->
        <div v-else class="empty-record">
          {{
            tr(
              "bchAccount.treasury.ledger.empty",
              "当前分类暂无资金记录"
            )
          }}
        </div>

        <!-- 加载下一页 -->
        <button
          v-if="walletConnected && list.length && !finished"
          type="button"
          class="load-more-button"
          :disabled="loadingMore"
          @click="loadAccountDetails(false)"
        >
          {{
            loadingMore
              ? tr("bchAccount.message.loadingMore", "正在同步...")
              : tr("bchAccount.treasury.action.loadMore", "加载更多")
          }}
        </button>

        <div
          v-if="walletConnected && list.length && finished"
          class="ledger-finished"
        >
          {{ tr("bchAccount.treasury.message.noMore", "没有更多记录了") }}
        </div>
      </section>

      <AppFooter />
    </main>

    <!-- ================= 提现弹窗 ================= -->
    <transition name="modal-fade">
      <div
        v-if="open"
        class="modal-mask"
        @click.self="closeWithdraw"
      >
        <section class="withdraw-modal">
          <div class="modal-glow"></div>

          <header class="modal-header">
            <div>
              <span class="modal-tag">ASSET TRANSFER</span>
              <h2>
                {{ tr("bchAccount.treasury.transfer.title", "资产划转申请") }}
              </h2>
              <p>
                {{
                  tr(
                    "bchAccount.treasury.transfer.subtitle",
                    "提交 USDT 外部资产划转指令"
                  )
                }}
              </p>
            </div>

            <button
              type="button"
              class="modal-close"
              :disabled="submitting"
              @click="closeWithdraw"
            >
              ×
            </button>
          </header>

          <div class="modal-balance">
            <span>
              {{ tr("bchAccount.treasury.balance.available", "可用结算余额") }}
            </span>
            <strong>
              {{ money(availableBalance, 4) }} {{ coinType }}
            </strong>
          </div>

          <div class="field-group">
            <label>
              {{ tr("bchAccount.treasury.transfer.amount", "划转金额") }}
            </label>

            <div class="amount-input">
              <input
                v-model="amount"
                type="text"
                inputmode="decimal"
                autocomplete="off"
                :disabled="submitting"
                :placeholder="
                  tr(
                    'bchAccount.withdraw.amountPlaceholder',
                    '请输入 USDT 金额'
                  )
                "
                @input="handleAmountInput"
              />

              <button
                type="button"
                class="max-button"
                :disabled="submitting"
                @click="useAllBalance"
              >
                {{ tr("bchAccount.action.all", "全部") }}
              </button>

              <span>{{ coinType }}</span>
            </div>
          </div>

          <div class="withdraw-settlement">
            <div>
              <span>
                {{ tr("bchAccount.treasury.transfer.requestAmount", "本次划转金额") }}
              </span>
              <strong>
                {{ money(validAmount || "0", 8) }} {{ coinType }}
              </strong>
            </div>

            <small>
              {{
                tr(
                  "bchAccount.treasury.transfer.settlementTip",
                  "提交后将进入合规校验、账户记账与链上资产结算流程。"
                )
              }}
            </small>
          </div>

          <div class="modal-notice">
            {{
              tr(
                "bchAccount.treasury.transfer.notice",
                "请核对划转金额。指令提交后，可在“资产划转”分类中查看审核、处理与链上结算状态。"
              )
            }}
          </div>

          <button
            type="button"
            class="confirm-button"
            :disabled="!canWithdraw"
            @click="submitWithdraw"
          >
            {{
              submitting
                ? tr("bchAccount.treasury.transfer.submitting", "正在提交划转指令...")
                : tr("bchAccount.treasury.transfer.confirm", "确认资产划转")
            }}
          </button>

          <button
            type="button"
            class="cancel-button"
            :disabled="submitting"
            @click="closeWithdraw"
          >
            {{ tr("bchAccount.action.cancel", "取消") }}
          </button>
        </section>
      </div>
    </transition>
  </div>
</template>

<script>
import AppHeader from "@/components/AppHeader.vue";
import AppFooter from "@/components/AppFooter.vue";

import {
  fetchBchAccountOverview,
  fetchBchAccountDetails
} from "@/api/bchAccount";

import {
  applyWithdraw
} from "@/api/income";

const DEFAULT_TABS = [
  {
    value: "all",
    labelKey: "bchAccount.category.all",
    fallback: "全部"
  },
  {
    value: "income",
    labelKey: "bchAccount.category.income",
    fallback: "策略结算"
  },
  {
    value: "withdraw",
    labelKey: "bchAccount.category.withdraw",
    fallback: "资产划转"
  },
  {
    value: "team",
    labelKey: "bchAccount.category.team",
    fallback: "网络协作"
  },
  {
    value: "direct",
    labelKey: "bchAccount.category.direct",
    fallback: "直接协作"
  }
];

export default {
  name: "AccountLedger",

  components: {
    AppHeader,
    AppFooter
  },

  data() {
    return {
      // 钱包状态
      walletConnected: false,
      walletAddress: "",
      walletSessionId: 0,

      // 账户概览：金额统一保留为字符串，避免金融金额被 JS 浮点数修改
      coinType: "USDT",
      availableBalance: "0",
      frozenAmount: "0",
      pendingAmount: "0",
      totalCashAmount: "0",
      overviewLoading: false,

      // 流水分类与分页
      activeTab: "all",
      tabs: DEFAULT_TABS.map(function(item) {
        return Object.assign({}, item);
      }),
      list: [],
      current: 1,
      size: 10,
      total: 0,
      pages: 0,
      finished: true,
      listLoading: false,
      loadingMore: false,

      // 当前筛选条件的统计
      summary: {
        successIncomeAmount: "0",
        successExpenseAmount: "0",
        successNetAmount: "0",
        successCount: 0,
        processingCount: 0
      },

      // 提现
      open: false,
      amount: "",
      submitting: false,

      // 事件引用，便于销毁
      visibilityHandler: null,
      focusHandler: null
    };
  },

  computed: {
    withdrawDisabled() {
      return (
        !this.walletConnected ||
        this.overviewLoading ||
        !this.isPositiveDecimal(this.availableBalance)
      );
    },

    withdrawButtonText() {
      if (!this.walletConnected) {
        return this.tr(
          "bchAccount.treasury.wallet.connectFirst",
          "请先连接数字钱包"
        );
      }

      if (this.overviewLoading) {
        return this.tr(
          "bchAccount.message.loading",
          "正在同步..."
        );
      }

      if (!this.isPositiveDecimal(this.availableBalance)) {
        return this.tr(
          "bchAccount.treasury.transfer.noBalance",
          "暂无可划转余额"
        );
      }

      return this.tr(
        "bchAccount.treasury.transfer.button",
        "提交资产划转"
      );
    },

    validAmount() {
      const value = String(this.amount || "").trim();

      if (!/^\d+(\.\d{1,8})?$/.test(value)) {
        return "";
      }

      return value;
    },

    canWithdraw() {
      if (
        !this.walletConnected ||
        this.submitting ||
        !this.validAmount
      ) {
        return false;
      }

      return (
        this.isPositiveDecimal(this.validAmount) &&
        this.compareDecimal(
          this.validAmount,
          this.availableBalance
        ) <= 0
      );
    },

    netAmountClass() {
      const result = this.compareDecimal(
        this.summary.successNetAmount,
        "0"
      );

      if (result > 0) {
        return "summary-income";
      }

      if (result < 0) {
        return "summary-expense";
      }

      return "";
    }
  },

  mounted() {
    this.registerWalletListeners();
    this.syncWalletState(true);
  },

  beforeDestroy() {
    this.removeWalletListeners();
  },

  methods: {
    /**
     * =========================
     * 钱包连接守卫
     * =========================
     */

    registerWalletListeners() {
      const ethereum = window.ethereum;

      if (ethereum && typeof ethereum.on === "function") {
        ethereum.on(
          "accountsChanged",
          this.handleAccountsChanged
        );

        ethereum.on(
          "disconnect",
          this.handleProviderDisconnect
        );
      }

      this.focusHandler = () => {
        this.syncWalletState(false);
      };

      this.visibilityHandler = () => {
        if (document.visibilityState === "visible") {
          this.syncWalletState(false);
        }
      };

      window.addEventListener("focus", this.focusHandler);
      document.addEventListener(
        "visibilitychange",
        this.visibilityHandler
      );
    },

    removeWalletListeners() {
      const ethereum = window.ethereum;

      if (
        ethereum &&
        typeof ethereum.removeListener === "function"
      ) {
        ethereum.removeListener(
          "accountsChanged",
          this.handleAccountsChanged
        );

        ethereum.removeListener(
          "disconnect",
          this.handleProviderDisconnect
        );
      }

      if (this.focusHandler) {
        window.removeEventListener(
          "focus",
          this.focusHandler
        );
      }

      if (this.visibilityHandler) {
        document.removeEventListener(
          "visibilitychange",
          this.visibilityHandler
        );
      }
    },

    async syncWalletState(forceReload) {
      const ethereum = window.ethereum;

      if (
        !ethereum ||
        typeof ethereum.request !== "function"
      ) {
        this.applyDisconnectedState();
        return;
      }

      try {
        // eth_accounts 不会主动弹出钱包授权框，只查询当前已连接账户
        const accounts = await ethereum.request({
          method: "eth_accounts"
        });

        const address =
          Array.isArray(accounts) && accounts.length
            ? accounts[0]
            : "";

        await this.applyWalletAddress(
          address,
          Boolean(forceReload)
        );
      } catch (error) {
        console.error(
          "[BCH-ACCOUNT] detect wallet failed:",
          error
        );

        this.applyDisconnectedState();
      }
    },

    async handleAccountsChanged(accounts) {
      const address =
        Array.isArray(accounts) && accounts.length
          ? accounts[0]
          : "";

      await this.applyWalletAddress(address, true);
    },

    handleProviderDisconnect() {
      this.applyDisconnectedState();
    },

    async applyWalletAddress(address, forceReload) {
      const normalizedAddress = this.normalizeAddress(address);

      if (!normalizedAddress) {
        this.applyDisconnectedState();
        return;
      }

      const accountChanged =
        normalizedAddress !== this.walletAddress;

      this.walletConnected = true;
      this.walletAddress = normalizedAddress;

      if (accountChanged || forceReload) {
        this.walletSessionId += 1;
        this.resetFinancialData();

        await Promise.all([
          this.loadAccountOverview(),
          this.loadAccountDetails(true)
        ]);
      }
    },

    applyDisconnectedState() {
      const wasConnected =
        this.walletConnected || Boolean(this.walletAddress);

      this.walletConnected = false;
      this.walletAddress = "";
      this.walletSessionId += 1;
      this.open = false;
      this.amount = "";
      this.submitting = false;
      this.overviewLoading = false;
      this.listLoading = false;
      this.loadingMore = false;

      this.resetFinancialData();

      if (wasConnected) {
        console.info(
          "[BCH-ACCOUNT] wallet disconnected, financial data cleared"
        );
      }
    },

    resetFinancialData() {
      this.coinType = "USDT";
      this.availableBalance = "0";
      this.frozenAmount = "0";
      this.pendingAmount = "0";
      this.totalCashAmount = "0";

      this.list = [];
      this.current = 1;
      this.total = 0;
      this.pages = 0;
      this.finished = true;

      this.summary = {
        successIncomeAmount: "0",
        successExpenseAmount: "0",
        successNetAmount: "0",
        successCount: 0,
        processingCount: 0
      };
    },

    normalizeAddress(address) {
      const value = String(address || "").trim();

      if (!/^0x[a-fA-F0-9]{40}$/.test(value)) {
        return "";
      }

      return value.toLowerCase();
    },

    /**
     * =========================
     * 账户概览
     * =========================
     */

    async loadAccountOverview() {
      if (!this.walletConnected || this.overviewLoading) {
        return;
      }

      const requestSessionId = this.walletSessionId;
      this.overviewLoading = true;

      try {
        const response = await fetchBchAccountOverview({
          silent: true
        });

        if (
          !this.walletConnected ||
          requestSessionId !== this.walletSessionId
        ) {
          return;
        }

        const data = this.unwrapApiData(response);

        this.coinType = data.coinType || "USDT";
        this.availableBalance = this.decimalString(
          data.availableBalance
        );
        this.frozenAmount = this.decimalString(
          data.frozenAmount
        );
        this.pendingAmount = this.decimalString(
          data.pendingAmount
        );
        this.totalCashAmount = this.decimalString(
          data.totalCashAmount
        );

        this.applyCategories(data.categories);
      } catch (error) {
        if (requestSessionId !== this.walletSessionId) {
          return;
        }

        console.error(
          "[BCH-ACCOUNT] load overview failed:",
          error
        );

        // 金融页面请求失败时不能继续展示旧余额
        this.availableBalance = "0";
        this.frozenAmount = "0";
        this.pendingAmount = "0";
        this.totalCashAmount = "0";

        this.showFeedback(
          this.errorMessage(
            error,
            this.tr(
              "bchAccount.treasury.message.loadOverviewFailed",
              "资产账户概览加载失败"
            )
          ),
          "error"
        );
      } finally {
        if (requestSessionId === this.walletSessionId) {
          this.overviewLoading = false;
        }
      }
    },

    applyCategories(categories) {
      if (!Array.isArray(categories) || !categories.length) {
        this.tabs = DEFAULT_TABS.map(function(item) {
          return Object.assign({}, item);
        });
        return;
      }

      const fallbackMap = DEFAULT_TABS.reduce(
        function(result, item) {
          result[item.value] = item.fallback;
          return result;
        },
        {}
      );

      const normalized = categories
        .filter(function(item) {
          return item && item.code;
        })
        .map(function(item) {
          return {
            value: item.code,
            labelKey: item.i18nKey || "",
            fallback:
              fallbackMap[item.code] || item.code
          };
        });

      this.tabs = normalized.length
        ? normalized
        : DEFAULT_TABS.map(function(item) {
            return Object.assign({}, item);
          });
    },

    /**
     * =========================
     * 账户流水分页
     * =========================
     */

    async loadAccountDetails(reset) {
      const shouldReset = Boolean(reset);

      if (!this.walletConnected) {
        this.list = [];
        this.finished = true;
        return;
      }

      if (this.listLoading || this.loadingMore) {
        return;
      }

      if (!shouldReset && this.finished) {
        return;
      }

      const requestSessionId = this.walletSessionId;
      const requestPage = shouldReset ? 1 : this.current;

      if (shouldReset) {
        this.listLoading = true;
        this.list = [];
        this.current = 1;
        this.total = 0;
        this.pages = 0;
        this.finished = false;
      } else {
        this.loadingMore = true;
      }

      try {
        const response = await fetchBchAccountDetails(
          {
            current: requestPage,
            size: this.size,
            category: this.activeTab
          },
          {
            silent: true
          }
        );

        if (
          !this.walletConnected ||
          requestSessionId !== this.walletSessionId
        ) {
          return;
        }

        const data = this.unwrapApiData(response);
        const records = Array.isArray(data.records)
          ? data.records
          : [];

        this.list = shouldReset
          ? records
          : this.list.concat(records);

        this.total = Number(data.total || 0);
        this.pages = Number(data.pages || 0);

        const responseCurrent = Number(
          data.current || requestPage
        );

        this.current = responseCurrent + 1;

        this.finished =
          records.length < this.size ||
          (
            this.pages > 0 &&
            responseCurrent >= this.pages
          );

        this.applySummary(data.summary);
      } catch (error) {
        if (requestSessionId !== this.walletSessionId) {
          return;
        }

        console.error(
          "[BCH-ACCOUNT] load details failed:",
          error
        );

        if (shouldReset) {
          // 请求失败时清空旧流水，防止切换账户或分类后展示错误数据
          this.list = [];
          this.current = 1;
          this.total = 0;
          this.pages = 0;
          this.finished = true;
          this.applySummary(null);
        }

        this.showFeedback(
          this.errorMessage(
            error,
            this.tr(
              "bchAccount.treasury.message.loadDetailsFailed",
              "资产分类账加载失败"
            )
          ),
          "error"
        );
      } finally {
        if (requestSessionId === this.walletSessionId) {
          this.listLoading = false;
          this.loadingMore = false;
        }
      }
    },

    applySummary(summary) {
      const data = summary || {};

      this.summary = {
        successIncomeAmount: this.decimalString(
          data.successIncomeAmount
        ),
        successExpenseAmount: this.decimalString(
          data.successExpenseAmount
        ),
        successNetAmount: this.decimalString(
          data.successNetAmount
        ),
        successCount: Number(data.successCount || 0),
        processingCount: Number(data.processingCount || 0)
      };
    },

    changeTab(category) {
      if (
        !this.walletConnected ||
        !category ||
        category === this.activeTab ||
        this.listLoading
      ) {
        return;
      }

      this.activeTab = category;
      this.loadAccountDetails(true);
    },

    /**
     * =========================
     * 提现
     * =========================
     */

    openWithdraw() {
      if (!this.walletConnected) {
        this.showFeedback(
          this.tr(
            "bchAccount.treasury.wallet.connectFirst",
            "请先连接数字钱包"
          ),
          "error"
        );
        return;
      }

      if (!this.isPositiveDecimal(this.availableBalance)) {
        this.showFeedback(
          this.tr(
            "bchAccount.treasury.transfer.noBalanceTip",
            "当前账户暂无可划转余额"
          ),
          "error"
        );
        return;
      }

      this.amount = "";
      this.open = true;
    },

    closeWithdraw() {
      if (this.submitting) {
        return;
      }

      this.open = false;
      this.amount = "";
    },

    handleAmountInput(event) {
      let value = String(
        event && event.target
          ? event.target.value
          : this.amount || ""
      );

      value = value.replace(/[^\d.]/g, "");

      const firstDotIndex = value.indexOf(".");

      if (firstDotIndex >= 0) {
        value =
          value.slice(0, firstDotIndex + 1) +
          value
            .slice(firstDotIndex + 1)
            .replace(/\./g, "");
      }

      const parts = value.split(".");

      if (parts.length > 1) {
        value =
          parts[0] +
          "." +
          parts[1].slice(0, 8);
      }

      // 避免输入多个无意义前导零，同时保留 0.x
      const normalizedParts = value.split(".");
      normalizedParts[0] = (normalizedParts[0] || "0").replace(
        /^0+(?=\d)/,
        ""
      );

      this.amount =
        normalizedParts.length > 1
          ? normalizedParts[0] + "." + normalizedParts[1]
          : normalizedParts[0];
    },

    useAllBalance() {
      if (this.submitting) {
        return;
      }

      this.amount = this.trimDecimal(
        this.availableBalance,
        8
      );
    },

    async submitWithdraw() {
      if (!this.walletConnected) {
        this.showFeedback(
          this.tr(
            "bchAccount.treasury.wallet.connectFirst",
            "请先连接数字钱包"
          ),
          "error"
        );
        return;
      }

      if (!this.canWithdraw) {
        const exceedsBalance =
          this.validAmount &&
          this.compareDecimal(
            this.validAmount,
            this.availableBalance
          ) > 0;

        this.showFeedback(
          exceedsBalance
            ? this.tr(
                "bchAccount.treasury.transfer.exceedsBalance",
                "划转金额不能超过可用结算余额"
              )
            : this.tr(
                "bchAccount.treasury.transfer.invalidAmount",
                "请输入有效的资产划转金额"
              ),
          "error"
        );
        return;
      }

      const requestSessionId = this.walletSessionId;
      const withdrawAmount = this.validAmount;

      this.submitting = true;

      try {
        await applyWithdraw(
          {
            coinType: this.coinType || "USDT",
            amount: withdrawAmount,
            reason: "BCH_ACCOUNT_WITHDRAW"
          },
          {
            successMsg: this.tr(
              "bchAccount.treasury.transfer.success",
              "资产划转指令已提交"
            )
          }
        );

        if (
          !this.walletConnected ||
          requestSessionId !== this.walletSessionId
        ) {
          return;
        }

        this.open = false;
        this.amount = "";

        // 提现完成后刷新余额，并切到提现分类查看最新状态
        this.activeTab = "withdraw";

        await Promise.all([
          this.loadAccountOverview(),
          this.loadAccountDetails(true)
        ]);
      } catch (error) {
        if (requestSessionId !== this.walletSessionId) {
          return;
        }

        console.error(
          "[BCH-ACCOUNT] withdraw failed:",
          error
        );

        this.showFeedback(
          this.errorMessage(
            error,
            this.tr(
              "bchAccount.treasury.transfer.failed",
              "资产划转指令提交失败"
            )
          ),
          "error"
        );
      } finally {
        if (requestSessionId === this.walletSessionId) {
          this.submitting = false;
        }
      }
    },

    /**
     * =========================
     * 流水展示与国际化
     * =========================
     */

    recordTitle(item) {
      const fallback = this.tradeTypeFallback(
        item && item.tradeType,
        "title"
      );

      return this.tr(
        item && item.titleI18nKey,
        fallback
      );
    },

    recordDescription(item) {
      const fallback = this.tradeTypeFallback(
        item && item.tradeType,
        "desc"
      );

      const translated = this.tr(
        item && item.descI18nKey,
        ""
      );

      if (translated) {
        return translated;
      }

      return (
        (item && item.remark) ||
        fallback ||
        this.tr(
          "bchAccount.ledger.defaultDesc",
          "资产账户变动"
        )
      );
    },

    recordState(item) {
      const fallbackMap = {
        1: "待审核",
        2: "结算处理中",
        3: "已结算",
        4: "处理失败"
      };

      return this.tr(
        item && item.stateI18nKey,
        fallbackMap[item && item.state] || ""
      );
    },

    tradeTypeFallback(tradeType, field) {
      const map = {
        BCH_ARBITRAGE_DAILY_PROFIT: {
          title: "策略执行结算",
          desc: "数字资产策略每日结算结果入账"
        },
        BCH_STANDARD_PRINCIPAL_RELEASE: {
          title: "执行额度释放",
          desc: "标准策略执行额度释放入账"
        },
        BCH_TRIAL_PRINCIPAL_RETURN: {
          title: "体验策略额度返还",
          desc: "体验策略到期配置额度返还"
        },
        BCH_TRIAL_PROFIT: {
          title: "体验策略结算",
          desc: "体验策略预设结算结果入账"
        },
        BCH_STANDARD_DIRECT_REWARD: {
          title: "直接协作结算",
          desc: "直接合作关系对应的协作结算"
        },
        BCH_TEAM_LEVEL_DIFF_REWARD: {
          title: "网络协作结算",
          desc: "合作网络层级差额结算"
        },
        BCH_WITHDRAW: {
          title: "资产划转",
          desc: "外部资产划转申请及链上结算记录"
        },
        BCH_WITHDRAW_FEE: {
          title: "划转服务费",
          desc: "资产划转与链上结算服务费用"
        }
      };

      const target = map[tradeType];

      return target
        ? target[field] || ""
        : field === "title"
          ? "资产账户变动"
          : "资产分类账记录";
    },

    directionStyle(item) {
      return Number(item && item.direction) === 2
        ? "withdraw"
        : "income";
    },

    directionSign(item) {
      return Number(item && item.direction) === 2
        ? "-"
        : "+";
    },

    formatTime(value) {
      if (!value) {
        return "--";
      }

      const text = String(value).replace("T", " ");

      return text.length >= 16
        ? text.slice(0, 16)
        : text;
    },

    /**
     * =========================
     * 金额处理
     * =========================
     */

    decimalString(value) {
      if (
        value === undefined ||
        value === null ||
        value === ""
      ) {
        return "0";
      }

      const text = String(value).trim();

      if (!/^-?\d+(\.\d+)?$/.test(text)) {
        return "0";
      }

      return text;
    },

    isPositiveDecimal(value) {
      return this.compareDecimal(value, "0") > 0;
    },

    compareDecimal(left, right) {
      const leftValue = this.toScaledBigInt(left, 18);
      const rightValue = this.toScaledBigInt(right, 18);

      if (leftValue > rightValue) {
        return 1;
      }

      if (leftValue < rightValue) {
        return -1;
      }

      return 0;
    },

    toScaledBigInt(value, scale) {
      const normalized = this.decimalString(value);
      const negative = normalized.charAt(0) === "-";
      const unsigned = negative
        ? normalized.slice(1)
        : normalized;

      const parts = unsigned.split(".");
      const integerPart = (parts[0] || "0").replace(
        /^0+(?=\d)/,
        ""
      );
      const fractionPart = (
        parts[1] || ""
      )
        .slice(0, scale)
        .padEnd(scale, "0");

      const result = BigInt(
        (integerPart || "0") + fractionPart
      );

      return negative ? -result : result;
    },

    trimDecimal(value, maxDigits) {
      const normalized = this.decimalString(value);
      const negative = normalized.charAt(0) === "-";
      const unsigned = negative
        ? normalized.slice(1)
        : normalized;
      const parts = unsigned.split(".");
      const integerPart = parts[0] || "0";
      let fractionPart = parts[1] || "";

      fractionPart = fractionPart
        .slice(0, maxDigits)
        .replace(/0+$/, "");

      return (
        (negative ? "-" : "") +
        integerPart +
        (fractionPart ? "." + fractionPart : "")
      );
    },

    money(value, maxDigits) {
      const digits =
        typeof maxDigits === "number"
          ? Math.max(0, maxDigits)
          : 2;

      const normalized = this.decimalString(value);
      const negative = normalized.charAt(0) === "-";
      const unsigned = negative
        ? normalized.slice(1)
        : normalized;

      const parts = unsigned.split(".");
      const integerPart = (parts[0] || "0").replace(
        /^0+(?=\d)/,
        ""
      );

      let fractionPart = (parts[1] || "")
        .slice(0, digits)
        .replace(/0+$/, "");

      const formattedInteger = integerPart.replace(
        /\B(?=(\d{3})+(?!\d))/g,
        ","
      );

      return (
        (negative ? "-" : "") +
        formattedInteger +
        (fractionPart ? "." + fractionPart : "")
      );
    },

    signedMoney(value, maxDigits) {
      const comparison = this.compareDecimal(value, "0");
      const formatted = this.money(value, maxDigits);

      if (comparison > 0) {
        return "+" + formatted;
      }

      return formatted;
    },

    /**
     * =========================
     * 公共辅助
     * =========================
     */

    unwrapApiData(response) {
      if (
        response &&
        response.data &&
        response.data.data !== undefined
      ) {
        return response.data.data || {};
      }

      if (
        response &&
        response.data !== undefined
      ) {
        return response.data || {};
      }

      return response || {};
    },

    tr(key, fallback) {
      if (!key) {
        return fallback || "";
      }

      if (typeof this.$t === "function") {
        const translated = this.$t(key);

        if (
          translated !== undefined &&
          translated !== null &&
          String(translated) !== key
        ) {
          return translated;
        }
      }

      return fallback || "";
    },

    errorMessage(error, fallback) {
      if (!error) {
        return fallback;
      }

      if (
        error.response &&
        error.response.data
      ) {
        return (
          error.response.data.msg ||
          error.response.data.message ||
          fallback
        );
      }

      return error.message || fallback;
    },

    showFeedback(message, type) {
      if (!message) {
        return;
      }

      if (this.$toast) {
        this.$toast(message);
        return;
      }

      if (this.$message) {
        const method =
          type === "error" ? "error" : "success";

        if (
          typeof this.$message[method] === "function"
        ) {
          this.$message[method](message);
          return;
        }
      }

      alert(message);
    }
  }
};
</script>

<style scoped>
/* =========================
   页面背景
========================= */

.bch-account {
  min-height: 100vh;
  color: #ffffff;

  background:
    radial-gradient(
      circle at 50% -8%,
      rgba(212, 175, 55, 0.18),
      transparent 28%
    ),
    radial-gradient(
      circle at 100% 18%,
      rgba(35, 113, 255, 0.18),
      transparent 32%
    ),
    linear-gradient(
      180deg,
      #05070d 0%,
      #070d18 46%,
      #0b1220 100%
    );
}

/*
 * 与首页相同：
 * 移动端左右各留 12px。
 */
.page-body {
  width: 100%;
  padding: 0 12px 28px;
  box-sizing: border-box;
}

/* =========================
   HERO
========================= */

.hero-premium {
  position: relative;
  min-height: 390px;
  padding: 30px 18px 24px;
  overflow: hidden;
  box-sizing: border-box;

  border: 1px solid rgba(255, 255, 255, 0.08);
  border-top: none;
  border-radius: 0 0 34px 34px;

  background:
    radial-gradient(
      circle at 76% 20%,
      rgba(212, 175, 55, 0.24),
      transparent 28%
    ),
    radial-gradient(
      circle at 20% 80%,
      rgba(37, 99, 235, 0.28),
      transparent 40%
    ),
    linear-gradient(
      145deg,
      #05070d 0%,
      #09111f 50%,
      #111827 100%
    );

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.04),
    0 26px 72px rgba(0, 0, 0, 0.48);
}

.hero-grid-bg {
  position: absolute;
  inset: 0;

  background-image:
    linear-gradient(
      rgba(255, 255, 255, 0.032) 1px,
      transparent 1px
    ),
    linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.032) 1px,
      transparent 1px
    );

  background-size: 30px 30px;
  opacity: 0.72;
}

.hero-glow-gold,
.hero-glow-blue {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  filter: blur(48px);
}

.hero-glow-gold {
  top: 70px;
  right: -90px;
  width: 220px;
  height: 220px;
  background: rgba(212, 175, 55, 0.26);
}

.hero-glow-blue {
  bottom: -90px;
  left: -100px;
  width: 240px;
  height: 240px;
  background: rgba(37, 99, 235, 0.26);
}

/* =========================
   B 发光轨道
========================= */

.arbitrage-orbit {
  position: absolute;
  z-index: 2;
  top: 34px;
  right: 18px;

  width: 142px;
  height: 142px;

  opacity: 0.95;
  pointer-events: none;
}

.orbit-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
}

.ring-a {
  border: 1px solid rgba(212, 175, 55, 0.3);

  box-shadow:
    0 0 24px rgba(212, 175, 55, 0.08),
    inset 0 0 20px rgba(212, 175, 55, 0.04);

  animation: orbitRotate 14s linear infinite;
}

.ring-b {
  inset: 18px;

  border: 1px solid rgba(59, 130, 246, 0.3);

  box-shadow:
    0 0 18px rgba(59, 130, 246, 0.08),
    inset 0 0 14px rgba(59, 130, 246, 0.04);

  animation: orbitReverse 10s linear infinite;
}

.ring-c {
  inset: 36px;
  border: 1px solid rgba(255, 255, 255, 0.14);
}

.orbit-core {
  position: absolute;
  inset: 47px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 50%;

  color: #080b12;
  font-size: 29px;
  font-weight: 950;
  line-height: 1;

  background:
    radial-gradient(
      circle at 28% 20%,
      #fff5c7,
      transparent 25%
    ),
    linear-gradient(
      135deg,
      #d4af37,
      #ffe08a
    );

  box-shadow:
    0 0 12px rgba(255, 224, 138, 0.8),
    0 0 32px rgba(212, 175, 55, 0.48),
    0 0 60px rgba(212, 175, 55, 0.2);

  animation: coreFloat 4.8s ease-in-out infinite;
}

.orbit-dot {
  position: absolute;
  width: 9px;
  height: 9px;
  border-radius: 50%;
}

.dot-a {
  top: 44px;
  right: 8px;

  background: #d4af37;

  box-shadow:
    0 0 8px rgba(212, 175, 55, 1),
    0 0 20px rgba(212, 175, 55, 0.8);

  animation: dotPulse 2.6s ease-in-out infinite;
}

.dot-b {
  bottom: 30px;
  left: 18px;

  background: #3b82f6;

  box-shadow:
    0 0 8px rgba(59, 130, 246, 1),
    0 0 20px rgba(59, 130, 246, 0.8);

  animation: dotPulse 2.6s ease-in-out 0.8s infinite;
}

@keyframes orbitRotate {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

@keyframes orbitReverse {
  from {
    transform: rotate(360deg);
  }

  to {
    transform: rotate(0deg);
  }
}

@keyframes coreFloat {
  0%,
  100% {
    transform: translateY(0) scale(1);
  }

  50% {
    transform: translateY(-6px) scale(1.04);
  }
}

@keyframes dotPulse {
  0%,
  100% {
    opacity: 0.6;
    transform: scale(0.88);
  }

  50% {
    opacity: 1;
    transform: scale(1.28);
  }
}

/* =========================
   HERO 文字
========================= */

.hero-content {
  position: relative;
  z-index: 5;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;

  max-width: 215px;
  padding: 9px 13px;

  border: 1px solid rgba(212, 175, 55, 0.34);
  border-radius: 999px;

  color: #ffe08a;
  font-size: 12px;
  font-weight: 900;

  background: rgba(212, 175, 55, 0.12);
}

.hero-badge i {
  flex: 0 0 auto;
  width: 7px;
  height: 7px;

  border-radius: 50%;
  background: #facc15;
  box-shadow: 0 0 14px rgba(250, 204, 21, 0.9);
}

.hero-premium h1 {
  max-width: 220px;
  margin: 22px 0 0;

  font-size: 38px;
  font-weight: 950;
  line-height: 1.08;
  letter-spacing: -1.4px;
}

.hero-title-main,
.hero-title-accent {
  display: block;
}

.hero-title-main {
  color: #ffffff;
}

.hero-title-accent {
  margin-top: 7px;

  color: transparent;

  background: linear-gradient(
    90deg,
    #d4af37 0%,
    #ffe08a 56%,
    #ffffff 100%
  );

  -webkit-background-clip: text;
  background-clip: text;
}

.hero-slogan {
  max-width: 680px;
  margin: 32px 0 0;

  color: #f5f7ff;
  font-size: 15px;
  font-weight: 900;
  line-height: 1.8;
}

.hero-desc {
  max-width: 680px;
  margin: 12px 0 0;

  color: #9fb3d7;
  font-size: 14px;
  line-height: 1.9;
}

/* =========================
   账户余额卡
========================= */

.balance-card {
  position: relative;
  margin-top: 14px;
  padding: 19px 16px;
  overflow: hidden;

  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 14px;

  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 24px;

  background:
    radial-gradient(
      circle at 100% 0,
      rgba(212, 175, 55, 0.13),
      transparent 42%
    ),
    linear-gradient(
      180deg,
      rgba(18, 28, 48, 0.92),
      rgba(8, 14, 26, 0.96)
    );

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.06),
    0 18px 48px rgba(0, 0, 0, 0.26);
}

.balance-card::before {
  position: absolute;
  top: 0;
  left: 22px;

  width: 90px;
  height: 2px;

  content: "";

  background: linear-gradient(
    90deg,
    transparent,
    #ffe08a,
    transparent
  );
}

.balance-content {
  min-width: 0;
}

.balance-label {
  display: block;

  color: #8ea4c8;
  font-size: 13px;
  font-weight: 700;
}

.balance-value {
  min-width: 0;
  margin-top: 7px;

  display: flex;
  align-items: baseline;
  gap: 7px;
}

.balance-value strong {
  min-width: 0;

  color: #ffffff;
  font-size: clamp(23px, 6.5vw, 36px);
  font-weight: 950;
  line-height: 1.15;
  letter-spacing: -1px;

  overflow-wrap: anywhere;
  font-variant-numeric: tabular-nums;
}

.balance-value span {
  flex: 0 0 auto;

  color: #ffe08a;
  font-size: 12px;
  font-weight: 900;
}

.withdraw-button {
  min-width: 108px;
  height: 48px;
  padding: 0 16px;

  border: none;
  border-radius: 16px;

  color: #080b12;
  font-size: 14px;
  font-weight: 950;

  background: linear-gradient(
    135deg,
    #d4af37 0%,
    #ffe08a 100%
  );

  box-shadow:
    0 12px 30px rgba(212, 175, 55, 0.24),
    inset 0 1px 0 rgba(255, 255, 255, 0.35);

  cursor: pointer;
}

.withdraw-button:active {
  transform: scale(0.97);
}

/* =========================
   账户记录
========================= */

.ledger-card {
  margin-top: 14px;
  padding: 18px 14px;

  border: 1px solid rgba(148, 163, 184, 0.15);
  border-radius: 28px;

  background: linear-gradient(
    180deg,
    rgba(18, 28, 48, 0.9),
    rgba(8, 14, 26, 0.95)
  );

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.06),
    0 18px 48px rgba(0, 0, 0, 0.26);
}

.ledger-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.ledger-header h2 {
  margin: 0;

  color: #ffffff;
  font-size: 22px;
  font-weight: 950;
}

.ledger-header p {
  margin: 7px 0 0;

  color: #8ea4c8;
  font-size: 13px;
  line-height: 1.6;
}

.ledger-badge {
  flex: 0 0 auto;
  height: 28px;
  padding: 0 10px;

  display: inline-flex;
  align-items: center;

  border: 1px solid rgba(212, 175, 55, 0.24);
  border-radius: 999px;

  color: #ffe08a;
  font-size: 10px;
  font-weight: 950;

  background: rgba(212, 175, 55, 0.1);
}

.ledger-tabs {
  margin-top: 16px;
  padding-bottom: 2px;

  display: flex;
  gap: 8px;
  overflow-x: auto;

  scrollbar-width: none;
}

.ledger-tabs::-webkit-scrollbar {
  display: none;
}

.ledger-tabs button {
  flex: 0 0 auto;
  min-width: 62px;
  height: 38px;
  padding: 0 14px;

  border: 1px solid rgba(255, 255, 255, 0.075);
  border-radius: 13px;

  color: #9fb3d7;
  font-size: 13px;
  font-weight: 800;

  background: rgba(255, 255, 255, 0.035);

  cursor: pointer;
}

.ledger-tabs button.active {
  border-color: rgba(212, 175, 55, 0.38);

  color: #ffe08a;

  background: linear-gradient(
    135deg,
    rgba(212, 175, 55, 0.2),
    rgba(212, 175, 55, 0.08)
  );
}

.ledger-list {
  margin-top: 14px;

  border: 1px solid rgba(255, 255, 255, 0.055);
  border-radius: 20px;

  overflow: hidden;
  background: rgba(3, 8, 17, 0.22);
}

.ledger-item {
  padding: 15px 14px;

  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
}

.ledger-item + .ledger-item {
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.record-left {
  min-width: 0;
}

.record-left strong {
  display: block;

  color: #ffffff;
  font-size: 15px;
  font-weight: 900;
}

.record-left span {
  display: block;
  margin-top: 7px;

  overflow: hidden;

  color: #8ea4c8;
  font-size: 12px;
  line-height: 1.5;

  text-overflow: ellipsis;
  white-space: nowrap;
}

.record-right {
  min-width: 108px;
  text-align: right;
}

.record-amount {
  display: block;

  font-size: 15px;
  font-weight: 950;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.record-amount.income {
  color: #2ee68a;
}

.record-amount.withdraw {
  color: #ff6b6b;
}

.record-amount small {
  font-size: 10px;
}

.record-right time {
  display: block;
  margin-top: 7px;

  color: #7184a7;
  font-size: 11px;
  white-space: nowrap;
}

.empty-record {
  margin-top: 14px;
  padding: 32px 16px;

  border: 1px dashed rgba(148, 163, 184, 0.15);
  border-radius: 18px;

  color: #7184a7;
  font-size: 13px;
  text-align: center;
}

/* =========================
   提现弹窗
========================= */

.modal-mask {
  position: fixed;
  z-index: 9999;
  inset: 0;

  padding: 18px;

  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;

  background: rgba(2, 5, 11, 0.82);

  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
}

.withdraw-modal {
  position: relative;
  width: 100%;
  max-width: 410px;
  padding: 22px;
  overflow: hidden;
  box-sizing: border-box;

  border: 1px solid rgba(212, 175, 55, 0.2);
  border-radius: 26px;

  background:
    radial-gradient(
      circle at 100% 0,
      rgba(212, 175, 55, 0.16),
      transparent 38%
    ),
    linear-gradient(
      180deg,
      rgba(18, 28, 48, 0.99),
      rgba(7, 12, 23, 0.99)
    );

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.07),
    0 32px 90px rgba(0, 0, 0, 0.72);
}

.modal-glow {
  position: absolute;
  top: -90px;
  right: -70px;

  width: 190px;
  height: 190px;

  border-radius: 50%;
  background: rgba(212, 175, 55, 0.14);

  filter: blur(45px);
  pointer-events: none;
}

.modal-header {
  position: relative;
  z-index: 2;

  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.modal-tag {
  color: #ffe08a;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 1.3px;
}

.modal-header h2 {
  margin: 7px 0 0;

  color: #ffffff;
  font-size: 25px;
  font-weight: 950;
}

.modal-header p {
  margin: 8px 0 0;

  color: #8ea4c8;
  font-size: 12px;
  line-height: 1.6;
}

.modal-close {
  flex: 0 0 auto;

  width: 36px;
  height: 36px;
  padding: 0;

  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 50%;

  color: #a9b8d3;
  font-size: 23px;
  line-height: 1;

  background: rgba(255, 255, 255, 0.045);
  cursor: pointer;
}

.modal-balance {
  position: relative;
  z-index: 2;

  margin-top: 20px;
  padding: 13px 14px;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  border: 1px solid rgba(255, 255, 255, 0.065);
  border-radius: 15px;

  background: rgba(255, 255, 255, 0.035);
}

.modal-balance span {
  color: #8ea4c8;
  font-size: 12px;
}

.modal-balance strong {
  color: #ffffff;
  font-size: 13px;
  font-weight: 900;
}

.field-group {
  position: relative;
  z-index: 2;
  margin-top: 17px;
}

.field-group label {
  display: block;
  margin-bottom: 9px;

  color: #dce6f8;
  font-size: 13px;
  font-weight: 800;
}

.amount-input {
  height: 58px;
  padding: 0 13px;

  display: flex;
  align-items: center;
  gap: 9px;

  border: 1px solid rgba(148, 163, 184, 0.17);
  border-radius: 17px;

  background: rgba(3, 8, 17, 0.52);
}

.amount-input:focus-within {
  border-color: rgba(212, 175, 55, 0.55);
  box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.08);
}

.amount-input input {
  min-width: 0;
  flex: 1;

  border: none;
  outline: none;

  color: #ffffff;
  font-size: 17px;
  font-weight: 800;

  background: transparent;
}

.amount-input input::placeholder {
  color: #627392;
  font-size: 14px;
  font-weight: 500;
}

.amount-input input::-webkit-inner-spin-button,
.amount-input input::-webkit-outer-spin-button {
  margin: 0;
  -webkit-appearance: none;
  appearance: none;
}

.amount-input > span {
  color: #ffffff;
  font-size: 13px;
  font-weight: 950;
}

.max-button {
  height: 30px;
  padding: 0 9px;

  border: 1px solid rgba(212, 175, 55, 0.26);
  border-radius: 9px;

  color: #ffe08a;
  font-size: 11px;
  font-weight: 900;

  background: rgba(212, 175, 55, 0.1);
  cursor: pointer;
}

.btc-result {
  position: relative;
  z-index: 2;

  margin-top: 13px;
  padding: 15px;

  border: 1px solid rgba(59, 130, 246, 0.15);
  border-radius: 17px;

  background: linear-gradient(
    135deg,
    rgba(37, 99, 235, 0.12),
    rgba(37, 99, 235, 0.035)
  );
}

.btc-result > div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.btc-result span {
  color: #8ea4c8;
  font-size: 12px;
}

.btc-result strong {
  color: #ffffff;
  font-size: 17px;
  font-weight: 950;
  font-variant-numeric: tabular-nums;
}

.btc-result small {
  display: block;
  margin-top: 8px;

  color: #7184a7;
  font-size: 11px;
}

.modal-notice {
  position: relative;
  z-index: 2;

  margin-top: 14px;
  padding: 12px;

  border: 1px solid rgba(212, 175, 55, 0.12);
  border-radius: 14px;

  color: #9fb3d7;
  font-size: 11px;
  line-height: 1.65;

  background: rgba(212, 175, 55, 0.055);
}

.confirm-button,
.cancel-button {
  position: relative;
  z-index: 2;

  width: 100%;
  border-radius: 16px;

  font-weight: 950;
  cursor: pointer;
}

.confirm-button {
  height: 50px;
  margin-top: 17px;

  border: none;

  color: #080b12;
  font-size: 15px;

  background: linear-gradient(
    135deg,
    #d4af37 0%,
    #ffe08a 100%
  );

  box-shadow: 0 14px 32px rgba(212, 175, 55, 0.24);
}

.confirm-button:disabled {
  color: rgba(255, 255, 255, 0.34);
  background: rgba(255, 255, 255, 0.08);
  box-shadow: none;
  cursor: not-allowed;
}

.cancel-button {
  height: 44px;
  margin-top: 10px;

  border: 1px solid rgba(255, 255, 255, 0.09);

  color: #a7b6cf;
  font-size: 13px;

  background: rgba(255, 255, 255, 0.035);
}

/* =========================
   弹窗动画
========================= */

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.22s ease;
}

.modal-fade-enter-active .withdraw-modal,
.modal-fade-leave-active .withdraw-modal {
  transition:
    opacity 0.22s ease,
    transform 0.22s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter .withdraw-modal,
.modal-fade-leave-to .withdraw-modal {
  opacity: 0;
  transform: translateY(16px) scale(0.98);
}

/* =========================
   小屏幕
========================= */

@media (max-width: 370px) {
  .hero-premium {
    padding-right: 14px;
    padding-left: 14px;
  }

  .arbitrage-orbit {
    right: 10px;
    width: 126px;
    height: 126px;
  }

  .ring-b {
    inset: 16px;
  }

  .ring-c {
    inset: 32px;
  }

  .orbit-core {
    inset: 42px;
    font-size: 25px;
  }

  .hero-premium h1 {
    max-width: 190px;
    font-size: 34px;
  }

  .balance-card {
    grid-template-columns: minmax(0, 1fr) 96px;
    padding: 18px 14px;
  }

  .withdraw-button {
    min-width: 96px;
    padding: 0 10px;
  }
}

/* =========================
   桌面端
========================= */

@media (min-width: 768px) {
  .page-body {
    max-width: 1180px;
    margin: 0 auto;
  }

  .hero-premium {
    min-height: 500px;
    padding: 52px 44px 36px;
  }

  .arbitrage-orbit {
    top: 64px;
    right: 70px;

    width: 220px;
    height: 220px;
  }

  .ring-b {
    inset: 28px;
  }

  .ring-c {
    inset: 56px;
  }

  .orbit-core {
    inset: 74px;
    font-size: 48px;
  }

  .dot-a {
    top: 65px;
    right: 15px;
  }

  .dot-b {
    bottom: 45px;
    left: 28px;
  }

  .hero-badge {
    max-width: none;
  }

  .hero-premium h1 {
    max-width: 620px;
    font-size: 58px;
  }

  .hero-slogan {
    margin-top: 26px;
    font-size: 17px;
  }

  .balance-card {
    padding: 25px 28px;
  }

  .withdraw-button {
    min-width: 150px;
    height: 52px;
  }

  .ledger-card {
    padding: 24px;
  }

  .ledger-item {
    padding: 18px;
  }
}

/* =========================
   实际接口对接补充样式
========================= */

.balance-main {
  min-width: 0;
}

.balance-head {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.wallet-state {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 24px;
  padding: 0 9px;

  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 999px;

  color: #91a5c8;
  font-size: 10px;
  font-weight: 850;

  background: rgba(255, 255, 255, 0.035);
}

.wallet-state i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.wallet-state.connected {
  border-color: rgba(46, 230, 138, 0.2);
  color: #61eca5;
  background: rgba(46, 230, 138, 0.07);
}

.wallet-state.connected i {
  background: #2ee68a;
  box-shadow: 0 0 10px rgba(46, 230, 138, 0.9);
}

.wallet-state.disconnected {
  border-color: rgba(255, 107, 107, 0.16);
  color: #ff9292;
  background: rgba(255, 107, 107, 0.055);
}

.wallet-state.disconnected i {
  background: #ff6b6b;
  box-shadow: 0 0 9px rgba(255, 107, 107, 0.65);
}

.balance-meta {
  margin-top: 14px;

  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.balance-meta-item {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.balance-meta-item span {
  color: #7184a7;
  font-size: 10px;
}

.balance-meta-item strong {
  color: #c9d6eb;
  font-size: 12px;
  font-weight: 850;
  font-variant-numeric: tabular-nums;
}

.balance-card.is-disconnected {
  border-color: rgba(148, 163, 184, 0.11);
}

.withdraw-button:disabled,
.ledger-tabs button:disabled,
.max-button:disabled,
.modal-close:disabled,
.cancel-button:disabled {
  opacity: 0.46;
  cursor: not-allowed;
}

.withdraw-button:disabled {
  color: #71809a;
  background: rgba(255, 255, 255, 0.07);
  box-shadow: none;
}

.ledger-summary {
  margin-top: 16px;
  padding: 12px;

  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;

  border: 1px solid rgba(255, 255, 255, 0.055);
  border-radius: 16px;

  background: rgba(3, 8, 17, 0.27);
}

.ledger-summary > div {
  min-width: 0;
  padding: 2px 7px;
}

.ledger-summary > div + div {
  border-left: 1px solid rgba(255, 255, 255, 0.06);
}

.ledger-summary span {
  display: block;

  color: #7184a7;
  font-size: 9px;
  line-height: 1.4;
}

.ledger-summary strong {
  display: block;
  margin-top: 5px;

  overflow: hidden;

  color: #dbe6f8;
  font-size: 12px;
  font-weight: 900;

  text-overflow: ellipsis;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.ledger-summary .summary-income {
  color: #2ee68a;
}

.ledger-summary .summary-expense {
  color: #ff7d7d;
}

.ledger-loading {
  margin-top: 14px;
  padding: 34px 16px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  border: 1px dashed rgba(148, 163, 184, 0.14);
  border-radius: 18px;
}

.loading-spinner {
  width: 24px;
  height: 24px;

  border: 2px solid rgba(255, 255, 255, 0.1);
  border-top-color: #ffe08a;
  border-radius: 50%;

  animation: accountSpin 0.8s linear infinite;
}

.ledger-loading p {
  margin: 10px 0 0;

  color: #7184a7;
  font-size: 12px;
}

@keyframes accountSpin {
  to {
    transform: rotate(360deg);
  }
}

.record-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.record-title-row > strong {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.record-state {
  flex: 0 0 auto;
  min-height: 20px;
  padding: 0 7px;

  display: inline-flex;
  align-items: center;

  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 999px;

  color: #8fa3c5;
  font-size: 8px;
  font-weight: 850;

  background: rgba(255, 255, 255, 0.035);
}

.record-state.state-1 {
  border-color: rgba(250, 204, 21, 0.16);
  color: #f7d65b;
  background: rgba(250, 204, 21, 0.055);
}

.record-state.state-2 {
  border-color: rgba(59, 130, 246, 0.18);
  color: #7eafff;
  background: rgba(59, 130, 246, 0.06);
}

.record-state.state-3 {
  border-color: rgba(46, 230, 138, 0.18);
  color: #5ceca3;
  background: rgba(46, 230, 138, 0.055);
}

.record-state.state-4 {
  border-color: rgba(255, 107, 107, 0.18);
  color: #ff9292;
  background: rgba(255, 107, 107, 0.055);
}

.wallet-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.wallet-empty strong {
  color: #dce6f8;
  font-size: 14px;
}

.wallet-empty span {
  max-width: 320px;
  color: #7184a7;
  font-size: 11px;
  line-height: 1.7;
}

.load-more-button {
  width: 100%;
  height: 43px;
  margin-top: 14px;

  border: 1px solid rgba(212, 175, 55, 0.18);
  border-radius: 14px;

  color: #ffe08a;
  font-size: 12px;
  font-weight: 850;

  background: rgba(212, 175, 55, 0.07);
  cursor: pointer;
}

.load-more-button:disabled {
  opacity: 0.55;
  cursor: wait;
}

.ledger-finished {
  margin-top: 13px;

  color: #596b8a;
  font-size: 10px;
  text-align: center;
}

.withdraw-settlement {
  position: relative;
  z-index: 2;

  margin-top: 13px;
  padding: 15px;

  border: 1px solid rgba(59, 130, 246, 0.15);
  border-radius: 17px;

  background: linear-gradient(
    135deg,
    rgba(37, 99, 235, 0.12),
    rgba(37, 99, 235, 0.035)
  );
}

.withdraw-settlement > div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.withdraw-settlement span {
  color: #8ea4c8;
  font-size: 12px;
}

.withdraw-settlement strong {
  color: #ffffff;
  font-size: 16px;
  font-weight: 950;
  font-variant-numeric: tabular-nums;
}

.withdraw-settlement small {
  display: block;
  margin-top: 8px;

  color: #7184a7;
  font-size: 11px;
  line-height: 1.55;
}

@media (max-width: 370px) {
  .balance-card {
    grid-template-columns: minmax(0, 1fr) 96px;
  }

  .balance-meta {
    gap: 12px;
  }

  .ledger-summary {
    padding: 10px 7px;
  }

  .ledger-summary > div {
    padding: 2px 5px;
  }

  .ledger-summary strong {
    font-size: 11px;
  }
}




/* =========================================================
 * BCH ASSET TREASURY — HOME THEME
 * 与首页完全统一：深蓝黑 + 科技蓝 + 低饱和金色
 * ========================================================= */

.bch-account {
  position: relative;
  isolation: isolate;
  overflow-x: hidden;

  background:
    radial-gradient(
      circle at 50% -10%,
      rgba(214, 177, 63, 0.18),
      transparent 26%
    ),
    radial-gradient(
      circle at 105% 22%,
      rgba(41, 108, 255, 0.18),
      transparent 31%
    ),
    radial-gradient(
      circle at -8% 68%,
      rgba(22, 78, 164, 0.13),
      transparent 30%
    ),
    linear-gradient(
      180deg,
      #03060c 0%,
      #07101d 42%,
      #091321 100%
    );
}

/* 与首页相同的全局科技网格 */
.account-grid-layer {
  position: fixed;
  z-index: -5;
  inset: 0;

  opacity: 0.34;
  pointer-events: none;

  background-image:
    linear-gradient(
      rgba(118, 151, 201, 0.032) 1px,
      transparent 1px
    ),
    linear-gradient(
      90deg,
      rgba(118, 151, 201, 0.032) 1px,
      transparent 1px
    );

  background-size: 36px 36px;

  -webkit-mask-image:
    linear-gradient(
      180deg,
      #000 0%,
      rgba(0, 0, 0, 0.78) 45%,
      transparent 100%
    );

  mask-image:
    linear-gradient(
      180deg,
      #000 0%,
      rgba(0, 0, 0, 0.78) 45%,
      transparent 100%
    );

  animation:
    accountHomeGridMove 28s linear infinite;
}

/* 页面环境扫描光 */
.account-scan-beam {
  position: fixed;
  z-index: -2;

  top: -22%;
  bottom: -22%;
  left: -190px;

  width: 150px;

  transform: skewX(-20deg);

  background: linear-gradient(
    90deg,
    transparent,
    rgba(75, 143, 255, 0.035),
    rgba(255, 224, 138, 0.055),
    rgba(255, 255, 255, 0.04),
    transparent
  );

  pointer-events: none;

  animation:
    accountHomeBeamSweep 12s ease-in-out infinite;
}

/* 环境光场：类名保留，颜色改为首页蓝金 */
.account-aurora {
  position: fixed;
  z-index: -4;

  border-radius: 50%;
  filter: blur(82px);

  pointer-events: none;
}

.account-aurora-cyan {
  top: 16%;
  right: -170px;

  width: 320px;
  height: 320px;

  background:
    rgba(41, 108, 255, 0.16);

  animation:
    accountHomeAurora 11s ease-in-out infinite;
}

.account-aurora-emerald {
  left: -180px;
  bottom: 9%;

  width: 330px;
  height: 330px;

  background:
    rgba(214, 177, 63, 0.11);

  animation:
    accountHomeAurora 13s ease-in-out infinite reverse;
}

/* 页面微粒：蓝金交替 */
.account-particles {
  position: fixed;
  z-index: -1;
  inset: 0;

  pointer-events: none;
}

.account-particles i {
  position: absolute;

  width: 3px;
  height: 3px;

  border-radius: 50%;

  background: #60a5fa;

  box-shadow:
    0 0 9px rgba(96, 165, 250, 0.86),
    0 0 17px rgba(59, 130, 246, 0.32);

  animation:
    accountHomeParticle 6.5s ease-in-out infinite;
}

.account-particles i:nth-child(1) {
  top: 15%;
  left: 7%;
}

.account-particles i:nth-child(2) {
  top: 33%;
  right: 8%;

  background: #ffe08a;

  box-shadow:
    0 0 9px rgba(255, 224, 138, 0.88),
    0 0 17px rgba(212, 175, 55, 0.3);

  animation-delay: 1.3s;
}

.account-particles i:nth-child(3) {
  top: 59%;
  left: 13%;

  background: #93c5fd;

  animation-delay: 2.5s;
}

.account-particles i:nth-child(4) {
  right: 12%;
  bottom: 18%;

  background: #d4af37;

  animation-delay: 3.7s;
}

.account-particles i:nth-child(5) {
  left: 48%;
  bottom: 7%;

  width: 2px;
  height: 2px;

  animation-delay: 4.5s;
}

.page-body {
  position: relative;
  z-index: 1;
}

/* =========================================================
 * HERO：直接采用首页的蓝金视觉体系
 * ========================================================= */

.hero-premium {
  isolation: isolate;

  min-height: 440px;

  border-color:
    rgba(151, 174, 214, 0.13);

  background:
    radial-gradient(
      circle at 80% 17%,
      rgba(214, 177, 63, 0.22),
      transparent 25%
    ),
    radial-gradient(
      circle at 11% 84%,
      rgba(36, 101, 226, 0.25),
      transparent 38%
    ),
    linear-gradient(
      148deg,
      #03060c 0%,
      #08111f 48%,
      #101a2a 100%
    );

  box-shadow:
    0 34px 90px rgba(0, 0, 0, 0.52),
    inset 0 1px 0 rgba(255, 255, 255, 0.055),
    inset 0 -1px 0 rgba(60, 130, 255, 0.08);

  animation:
    accountHomeSectionReveal 0.72s
    cubic-bezier(0.2, 0.75, 0.25, 1)
    both;
}

.hero-premium::before {
  position: absolute;
  z-index: 1;

  top: -35%;
  bottom: -35%;
  left: -32%;

  width: 24%;

  content: "";
  transform: skewX(-19deg);
  pointer-events: none;

  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.02),
    rgba(255, 226, 145, 0.08),
    rgba(255, 255, 255, 0.025),
    transparent
  );

  animation:
    accountHomeHeroSweep 8.8s ease-in-out infinite;
}

.hero-premium::after {
  position: absolute;
  z-index: 3;

  top: 0;
  left: 8%;
  right: 8%;

  height: 1px;

  content: "";
  pointer-events: none;

  background: linear-gradient(
    90deg,
    transparent,
    rgba(75, 143, 255, 0.55),
    rgba(255, 224, 138, 0.72),
    rgba(75, 143, 255, 0.55),
    transparent
  );

  background-size: 220% 100%;

  animation:
    accountHomeBorderFlow 5.6s linear infinite;
}

.hero-grid-bg {
  background-image:
    linear-gradient(
      rgba(118, 151, 201, 0.032) 1px,
      transparent 1px
    ),
    linear-gradient(
      90deg,
      rgba(118, 151, 201, 0.032) 1px,
      transparent 1px
    );

  background-size: 30px 30px;

  animation:
    accountHomeHeroGrid 24s linear infinite;
}

.hero-glow-gold {
  background:
    rgba(212, 175, 55, 0.26);

  animation:
    accountHomeGlowGold 7s ease-in-out infinite;
}

.hero-glow-blue {
  background:
    rgba(37, 99, 235, 0.26);

  animation:
    accountHomeGlowBlue 9s ease-in-out infinite;
}

.hero-scan-line {
  position: absolute;
  z-index: 2;

  top: -28%;
  bottom: -28%;
  left: -110px;

  width: 94px;

  transform: skewX(-22deg);

  background: linear-gradient(
    90deg,
    transparent,
    rgba(59, 130, 246, 0.045),
    rgba(255, 224, 138, 0.07),
    rgba(255, 255, 255, 0.06),
    transparent
  );

  animation:
    accountHomeHeroLine 9s ease-in-out infinite;
}

.hero-data-stream {
  position: absolute;
  z-index: 1;

  right: 3%;
  bottom: 8%;

  width: 46%;
  height: 90px;

  opacity: 0.3;

  pointer-events: none;
}

.hero-data-stream span {
  position: absolute;
  left: 0;
  right: 0;

  height: 1px;

  background: linear-gradient(
    90deg,
    transparent,
    rgba(75, 143, 255, 0.7),
    rgba(255, 224, 138, 0.58),
    transparent
  );

  transform-origin: left center;

  animation:
    accountHomeDataPulse 3.6s ease-in-out infinite;
}

.hero-data-stream span:nth-child(1) {
  top: 16%;
  transform: rotate(-5deg);
}

.hero-data-stream span:nth-child(2) {
  top: 49%;
  transform: rotate(4deg);
  animation-delay: 0.8s;
}

.hero-data-stream span:nth-child(3) {
  top: 78%;
  transform: rotate(-2deg);
  animation-delay: 1.5s;
}

.hero-content {
  animation:
    accountHomeContentRise 0.76s 0.06s
    cubic-bezier(0.2, 0.75, 0.25, 1)
    both;
}

.hero-badge {
  position: relative;
  overflow: hidden;

  color: #ffe08a;

  border-color:
    rgba(212, 175, 55, 0.34);

  background:
    rgba(212, 175, 55, 0.12);

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.08),
    0 10px 28px rgba(0, 0, 0, 0.16);

  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
}

.hero-badge::after {
  position: absolute;

  top: 0;
  bottom: 0;
  left: -42px;

  width: 28px;

  content: "";
  transform: skewX(-22deg);

  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );

  animation:
    accountHomeBadgeSweep 5.5s ease-in-out infinite;
}

.hero-badge i {
  background: #facc15;

  box-shadow:
    0 0 14px rgba(250, 204, 21, 0.9);

  animation:
    accountHomeStatusPulse 1.9s ease-in-out infinite;
}

.hero-title-accent {
  background: linear-gradient(
    90deg,
    #d4af37 0%,
    #ffe08a 54%,
    #ffffff 100%
  );

  background-size: 200% 100%;

  -webkit-background-clip: text;
  background-clip: text;

  animation:
    accountHomeTextFlow 6.5s linear infinite;
}

.hero-desc {
  color: #9fb3d7;
}

/* Hero 下方三个专业能力标签 */
.hero-capability-grid {
  position: relative;
  z-index: 4;

  margin-top: 24px;

  display: grid;
  grid-template-columns:
    repeat(3, minmax(0, 1fr));

  gap: 8px;
}

.hero-capability-grid > div {
  position: relative;
  overflow: hidden;

  min-width: 0;
  min-height: 69px;

  padding: 12px 8px;

  border:
    1px solid rgba(137, 163, 206, 0.13);

  border-radius: 16px;

  background:
    radial-gradient(
      circle at 100% 0%,
      rgba(43, 104, 209, 0.07),
      transparent 38%
    ),
    linear-gradient(
      145deg,
      rgba(255, 255, 255, 0.052),
      rgba(255, 255, 255, 0.02)
    );

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.05),
    0 12px 28px rgba(0, 0, 0, 0.18);

  transition:
    transform 0.22s ease,
    border-color 0.22s ease,
    box-shadow 0.22s ease;
}

.hero-capability-grid > div::before {
  position: absolute;

  top: 0;
  left: 14%;
  right: 14%;

  height: 1px;

  content: "";

  background: linear-gradient(
    90deg,
    transparent,
    rgba(75, 143, 255, 0.48),
    rgba(255, 224, 138, 0.38),
    transparent
  );
}

.hero-capability-grid > div:hover {
  transform: translateY(-3px);

  border-color:
    rgba(100, 153, 236, 0.27);

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.07),
    0 18px 38px rgba(0, 0, 0, 0.28),
    0 0 26px rgba(38, 101, 206, 0.06);
}

.hero-capability-grid small,
.hero-capability-grid strong {
  display: block;
}

.hero-capability-grid small {
  overflow: hidden;

  color: #687a98;

  font-family:
    SFMono-Regular,
    Consolas,
    "Liberation Mono",
    monospace;

  font-size: 7px;
  font-weight: 900;
  letter-spacing: 0.45px;

  white-space: nowrap;
  text-overflow: ellipsis;
}

.hero-capability-grid strong {
  margin-top: 7px;

  color: #e7eefb;

  font-size: 10px;
  font-weight: 850;
  line-height: 1.4;
}

/* BTC/BCH 轨道与首页统一 */
.ring-a {
  border-color:
    rgba(212, 175, 55, 0.3);

  box-shadow:
    0 0 24px rgba(212, 175, 55, 0.08),
    inset 0 0 20px rgba(212, 175, 55, 0.04);
}

.ring-b {
  border-color:
    rgba(59, 130, 246, 0.3);

  box-shadow:
    0 0 18px rgba(59, 130, 246, 0.08),
    inset 0 0 14px rgba(59, 130, 246, 0.04);
}

.orbit-core {
  color: #080b12;

  background:
    radial-gradient(
      circle at 28% 20%,
      #fff5c7,
      transparent 25%
    ),
    linear-gradient(
      135deg,
      #d4af37,
      #ffe08a
    );

  box-shadow:
    0 0 12px rgba(255, 224, 138, 0.8),
    0 0 32px rgba(212, 175, 55, 0.48),
    0 0 60px rgba(212, 175, 55, 0.2);
}

.dot-a {
  background: #d4af37;

  box-shadow:
    0 0 8px rgba(212, 175, 55, 1),
    0 0 20px rgba(212, 175, 55, 0.8);
}

.dot-b {
  background: #3b82f6;

  box-shadow:
    0 0 8px rgba(59, 130, 246, 1),
    0 0 20px rgba(59, 130, 246, 0.8);
}

/* =========================================================
 * 资产账户卡：维持专业结构，视觉切回首页蓝金
 * ========================================================= */

.balance-card {
  isolation: isolate;

  padding: 21px 16px;

  border-color:
    rgba(137, 163, 206, 0.15);

  background:
    radial-gradient(
      circle at 100% 0%,
      rgba(214, 177, 63, 0.14),
      transparent 38%
    ),
    radial-gradient(
      circle at 0% 100%,
      rgba(37, 99, 235, 0.1),
      transparent 40%
    ),
    linear-gradient(
      180deg,
      rgba(17, 28, 48, 0.93),
      rgba(7, 14, 26, 0.96)
    );

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.055),
    0 20px 52px rgba(0, 0, 0, 0.28);
}

.balance-card::before {
  left: 10%;
  right: 10%;

  width: auto;

  background: linear-gradient(
    90deg,
    transparent,
    rgba(77, 145, 255, 0.56),
    rgba(255, 224, 138, 0.58),
    transparent
  );

  background-size: 200% 100%;

  animation:
    accountHomeBorderFlow 7s linear infinite;
}

.balance-card::after {
  position: absolute;

  top: -24px;
  bottom: -24px;
  left: -50px;

  width: 34px;

  content: "";

  transform: skewX(-22deg);

  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 224, 138, 0.08),
    rgba(255, 255, 255, 0.07),
    transparent
  );

  animation:
    accountHomeCardSweep 7.2s ease-in-out infinite;
}

.balance-kicker {
  margin-bottom: 9px;

  display: inline-flex;
  align-items: center;
  gap: 7px;

  color: #7d90af;

  font-family:
    SFMono-Regular,
    Consolas,
    "Liberation Mono",
    monospace;

  font-size: 8px;
  font-weight: 900;
  letter-spacing: 0.8px;
}

.balance-kicker i {
  width: 6px;
  height: 6px;

  border-radius: 50%;

  background: #3b82f6;

  box-shadow:
    0 0 10px rgba(59, 130, 246, 0.86);

  animation:
    accountHomeStatusPulse 1.8s ease-in-out infinite;
}

.balance-label {
  color: #8ea4c8;
}

.balance-value strong {
  color: #ffffff;

  text-shadow:
    0 6px 24px rgba(0, 0, 0, 0.3);
}

.balance-value span {
  color: #ffe08a;
}

.balance-meta {
  gap: 12px 18px;
}

.balance-meta-item {
  min-width: 92px;

  padding-right: 15px;

  border-right:
    1px solid rgba(255, 255, 255, 0.055);
}

.balance-meta-item:last-child {
  padding-right: 0;
  border-right: none;
}

.balance-meta-item span {
  color: #7184a7;
}

.balance-meta-item strong {
  color: #c9d6eb;
}

.balance-meta-item.total-equity strong {
  color: #ffe08a;
}

.wallet-state.connected {
  color: #75f3a3;

  border-color:
    rgba(34, 197, 94, 0.2);

  background:
    rgba(34, 197, 94, 0.07);
}

.wallet-state.connected i {
  background: #22c55e;

  box-shadow:
    0 0 10px rgba(34, 197, 94, 0.88);
}

.withdraw-button {
  position: relative;
  overflow: hidden;
  isolation: isolate;

  color: #080b12;

  border:
    1px solid rgba(255, 243, 194, 0.58);

  background:
    linear-gradient(
      135deg,
      #bd9222 0%,
      #e0bc55 38%,
      #ffe49a 66%,
      #c79c2b 100%
    );

  background-size: 180% 180%;

  box-shadow:
    0 16px 36px rgba(197, 151, 34, 0.24),
    inset 0 1px 0 rgba(255, 255, 255, 0.62),
    inset 0 -1px 0 rgba(95, 66, 5, 0.18);

  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    background-position 0.25s ease;
}

.withdraw-button::before {
  position: absolute;
  z-index: -1;

  top: -18px;
  bottom: -18px;
  left: -54px;

  width: 36px;

  content: "";
  transform: skewX(-22deg);

  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.68),
    transparent
  );

  animation:
    accountHomeButtonSweep 5.2s ease-in-out infinite;
}

.withdraw-button:not(:disabled):hover {
  transform: translateY(-2px);

  background-position: 100% 50%;

  box-shadow:
    0 21px 44px rgba(197, 151, 34, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.72);
}

/* =========================================================
 * 资产分类账：首页卡片体系
 * ========================================================= */

.ledger-card {
  position: relative;
  overflow: hidden;

  border-color:
    rgba(137, 163, 206, 0.15);

  background:
    radial-gradient(
      circle at 100% 0%,
      rgba(43, 104, 209, 0.07),
      transparent 34%
    ),
    linear-gradient(
      180deg,
      rgba(17, 28, 48, 0.92),
      rgba(7, 14, 26, 0.96)
    );

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.055),
    0 20px 52px rgba(0, 0, 0, 0.28);
}

.ledger-card::before {
  position: absolute;

  top: 0;
  left: 10%;
  right: 10%;

  height: 1px;

  content: "";

  opacity: 0.58;

  background: linear-gradient(
    90deg,
    transparent,
    rgba(77, 145, 255, 0.56),
    rgba(255, 224, 138, 0.48),
    transparent
  );

  background-size: 200% 100%;

  animation:
    accountHomeBorderFlow 7s linear infinite;
}

.ledger-header h2 {
  color: #ffffff;
}

.ledger-header p {
  color: #8ea4c8;
}

.ledger-badge {
  color: #ffe08a;

  border-color:
    rgba(212, 175, 55, 0.24);

  background:
    rgba(212, 175, 55, 0.1);

  font-family:
    SFMono-Regular,
    Consolas,
    "Liberation Mono",
    monospace;

  letter-spacing: 0.45px;
}

.ledger-summary {
  border-color:
    rgba(137, 163, 206, 0.1);

  background:
    linear-gradient(
      145deg,
      rgba(37, 99, 235, 0.055),
      rgba(3, 8, 17, 0.34)
    );
}

.ledger-summary > div {
  position: relative;
}

.ledger-summary > div::after {
  position: absolute;

  left: 18%;
  right: 18%;
  bottom: -8px;

  height: 1px;

  content: "";
  opacity: 0;

  background: linear-gradient(
    90deg,
    transparent,
    #ffe08a,
    transparent
  );

  transition:
    opacity 0.2s ease;
}

.ledger-summary > div:hover::after {
  opacity: 0.65;
}

.ledger-summary .summary-income {
  color: #2ee68a;
}

.ledger-summary .summary-expense {
  color: #ff7d7d;
}

.ledger-tabs button {
  color: #9fb3d7;

  border-color:
    rgba(255, 255, 255, 0.075);

  background:
    rgba(255, 255, 255, 0.035);

  transition:
    transform 0.2s ease,
    color 0.2s ease,
    border-color 0.2s ease,
    background 0.2s ease;
}

.ledger-tabs button:not(:disabled):hover {
  transform: translateY(-1px);

  color: #ffffff;

  border-color:
    rgba(100, 153, 236, 0.23);
}

.ledger-tabs button.active {
  color: #ffe08a;

  border-color:
    rgba(212, 175, 55, 0.38);

  background:
    linear-gradient(
      135deg,
      rgba(212, 175, 55, 0.2),
      rgba(37, 99, 235, 0.055)
    );

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.04),
    0 8px 18px rgba(0, 0, 0, 0.14);
}

.ledger-list {
  border-color:
    rgba(126, 158, 209, 0.095);

  background:
    rgba(3, 8, 17, 0.3);
}

.ledger-item {
  position: relative;

  transition:
    background 0.2s ease,
    transform 0.2s ease;
}

.ledger-item::before {
  position: absolute;

  top: 14px;
  bottom: 14px;
  left: 0;

  width: 2px;

  border-radius:
    0 999px 999px 0;

  content: "";
  opacity: 0;

  background: linear-gradient(
    180deg,
    #3b82f6,
    #ffe08a
  );

  transition:
    opacity 0.2s ease;
}

.ledger-item:hover {
  background:
    rgba(37, 99, 235, 0.038);
}

.ledger-item:hover::before {
  opacity: 0.9;
}

.record-left strong {
  color: #ffffff;
}

.record-left span {
  color: #8ea4c8;
}

.record-amount.income {
  color: #2ee68a;
}

.record-amount.withdraw {
  color: #ff7d7d;
}

.record-state.state-2 {
  color: #93c5fd;

  border-color:
    rgba(59, 130, 246, 0.18);

  background:
    rgba(59, 130, 246, 0.06);
}

.record-state.state-3 {
  color: #5ceca3;

  border-color:
    rgba(46, 230, 138, 0.18);

  background:
    rgba(46, 230, 138, 0.055);
}

.loading-spinner {
  border-top-color: #ffe08a;

  box-shadow:
    0 0 14px rgba(212, 175, 55, 0.1);
}

.load-more-button {
  color: #ffe08a;

  border-color:
    rgba(212, 175, 55, 0.18);

  background:
    rgba(212, 175, 55, 0.07);

  transition:
    transform 0.2s ease,
    border-color 0.2s ease,
    background 0.2s ease;
}

.load-more-button:not(:disabled):hover {
  transform: translateY(-1px);

  border-color:
    rgba(212, 175, 55, 0.3);

  background:
    rgba(212, 175, 55, 0.11);
}

/* =========================================================
 * 资产划转弹窗：与首页弹框相同蓝金体系
 * ========================================================= */

.modal-mask {
  background:
    rgba(1, 4, 10, 0.84);
}

.withdraw-modal {
  isolation: isolate;

  border-color:
    rgba(255, 224, 138, 0.22);

  background:
    radial-gradient(
      circle at 85% 3%,
      rgba(212, 175, 55, 0.2),
      transparent 30%
    ),
    radial-gradient(
      circle at 0% 100%,
      rgba(37, 99, 235, 0.18),
      transparent 36%
    ),
    linear-gradient(
      155deg,
      #101a2c 0%,
      #070d17 48%,
      #04070d 100%
    );

  box-shadow:
    0 34px 90px rgba(0, 0, 0, 0.7),
    inset 0 1px 0 rgba(255, 255, 255, 0.09),
    0 0 40px rgba(47, 107, 210, 0.06);
}

.withdraw-modal::before {
  position: absolute;

  top: 0;
  left: 10%;
  right: 10%;

  height: 1px;

  content: "";

  background: linear-gradient(
    90deg,
    transparent,
    rgba(75, 143, 255, 0.55),
    rgba(255, 224, 138, 0.72),
    rgba(75, 143, 255, 0.55),
    transparent
  );

  background-size: 220% 100%;

  animation:
    accountHomeBorderFlow 5.6s linear infinite;
}

.withdraw-modal::after {
  position: absolute;

  top: -25px;
  bottom: -25px;
  left: -48px;

  width: 31px;

  content: "";
  transform: skewX(-22deg);

  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 224, 138, 0.07),
    rgba(255, 255, 255, 0.06),
    transparent
  );

  animation:
    accountHomeModalSweep 8s ease-in-out infinite;
}

.modal-glow {
  background:
    rgba(212, 175, 55, 0.16);

  animation:
    accountHomeGlowGold 6s ease-in-out infinite;
}

.modal-tag {
  color: #ffe08a;

  text-shadow:
    0 0 12px rgba(255, 224, 138, 0.18);
}

.modal-header h2 {
  color: #ffffff;
}

.modal-header p {
  color: #8ea4c8;
}

.modal-close {
  border-color:
    rgba(123, 157, 209, 0.14);

  background:
    linear-gradient(
      145deg,
      rgba(20, 31, 52, 0.82),
      rgba(8, 14, 27, 0.92)
    );

  transition:
    transform 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease;
}

.modal-close:not(:disabled):hover {
  transform: translateY(-1px);

  color: #ffe08a;

  border-color:
    rgba(212, 175, 55, 0.28);
}

.modal-balance {
  border-color:
    rgba(123, 157, 209, 0.1);

  background:
    rgba(255, 255, 255, 0.035);
}

.amount-input {
  border-color:
    rgba(148, 163, 184, 0.17);

  background:
    rgba(3, 8, 17, 0.52);
}

.amount-input:focus-within {
  border-color:
    rgba(212, 175, 55, 0.55);

  box-shadow:
    0 0 0 3px rgba(212, 175, 55, 0.08),
    0 0 24px rgba(37, 99, 235, 0.045);
}

.max-button {
  color: #ffe08a;

  border-color:
    rgba(212, 175, 55, 0.26);

  background:
    rgba(212, 175, 55, 0.1);
}

.withdraw-settlement {
  border-color:
    rgba(59, 130, 246, 0.15);

  background:
    linear-gradient(
      135deg,
      rgba(37, 99, 235, 0.12),
      rgba(37, 99, 235, 0.035)
    );
}

.withdraw-settlement strong {
  color: #ffffff;
}

.modal-notice {
  color: #9fb3d7;

  border-color:
    rgba(212, 175, 55, 0.12);

  background:
    rgba(212, 175, 55, 0.055);
}

.confirm-button {
  position: relative;
  overflow: hidden;
  isolation: isolate;

  color: #080b12;

  border:
    1px solid rgba(255, 243, 194, 0.58);

  background:
    linear-gradient(
      135deg,
      #bd9222 0%,
      #e0bc55 38%,
      #ffe49a 66%,
      #c79c2b 100%
    );

  background-size: 180% 180%;

  box-shadow:
    0 16px 36px rgba(197, 151, 34, 0.24),
    inset 0 1px 0 rgba(255, 255, 255, 0.62);
}

.confirm-button::before {
  position: absolute;
  z-index: -1;

  top: -18px;
  bottom: -18px;
  left: -54px;

  width: 36px;

  content: "";
  transform: skewX(-22deg);

  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.68),
    transparent
  );

  animation:
    accountHomeButtonSweep 5.2s ease-in-out infinite;
}

.cancel-button {
  border-color:
    rgba(123, 157, 209, 0.1);

  background:
    rgba(255, 255, 255, 0.035);
}

/* =========================================================
 * 动画
 * ========================================================= */

@keyframes accountHomeGridMove {
  from {
    background-position:
      0 0,
      0 0;
  }

  to {
    background-position:
      36px 36px,
      36px 36px;
  }
}

@keyframes accountHomeBeamSweep {
  0%,
  18% {
    left: -200px;
    opacity: 0;
  }

  30% {
    opacity: 0.7;
  }

  68% {
    left: calc(100% + 190px);
    opacity: 0.2;
  }

  100% {
    left: calc(100% + 190px);
    opacity: 0;
  }
}

@keyframes accountHomeAurora {
  0%,
  100% {
    opacity: 0.48;

    transform:
      translate3d(0, 0, 0)
      scale(0.9);
  }

  50% {
    opacity: 0.92;

    transform:
      translate3d(18px, -18px, 0)
      scale(1.08);
  }
}

@keyframes accountHomeParticle {
  0%,
  100% {
    opacity: 0.2;

    transform:
      translateY(9px)
      scale(0.75);
  }

  50% {
    opacity: 1;

    transform:
      translateY(-12px)
      scale(1.18);
  }
}

@keyframes accountHomeSectionReveal {
  from {
    opacity: 0;

    transform:
      translateY(18px)
      scale(0.988);
  }

  to {
    opacity: 1;

    transform:
      translateY(0)
      scale(1);
  }
}

@keyframes accountHomeContentRise {
  from {
    opacity: 0;
    transform: translateY(16px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes accountHomeHeroSweep {
  0%,
  15% {
    left: -32%;
    opacity: 0;
  }

  25% {
    opacity: 0.9;
  }

  52% {
    left: 115%;
    opacity: 0;
  }

  100% {
    left: 115%;
    opacity: 0;
  }
}

@keyframes accountHomeBorderFlow {
  from {
    background-position:
      0% 50%;
  }

  to {
    background-position:
      220% 50%;
  }
}

@keyframes accountHomeHeroGrid {
  from {
    background-position:
      0 0,
      0 0;
  }

  to {
    background-position:
      60px 60px,
      60px 60px;
  }
}

@keyframes accountHomeGlowGold {
  0%,
  100% {
    opacity: 0.58;
    transform: scale(0.9);
  }

  50% {
    opacity: 1;
    transform: scale(1.12);
  }
}

@keyframes accountHomeGlowBlue {
  0%,
  100% {
    opacity: 0.54;
    transform: scale(0.9);
  }

  50% {
    opacity: 0.96;
    transform: scale(1.1);
  }
}

@keyframes accountHomeHeroLine {
  0%,
  18% {
    left: -120px;
    opacity: 0;
  }

  30% {
    opacity: 1;
  }

  58% {
    left: calc(100% + 120px);
    opacity: 0;
  }

  100% {
    left: calc(100% + 120px);
    opacity: 0;
  }
}

@keyframes accountHomeDataPulse {
  0%,
  100% {
    opacity: 0.15;
    transform: scaleX(0.78);
  }

  50% {
    opacity: 0.8;
    transform: scaleX(1);
  }
}

@keyframes accountHomeBadgeSweep {
  0%,
  22% {
    left: -42px;
    opacity: 0;
  }

  32% {
    opacity: 1;
  }

  52% {
    left: calc(100% + 42px);
    opacity: 0;
  }

  100% {
    left: calc(100% + 42px);
    opacity: 0;
  }
}

@keyframes accountHomeStatusPulse {
  0%,
  100% {
    opacity: 0.48;
    transform: scale(0.82);
  }

  50% {
    opacity: 1;
    transform: scale(1.18);
  }
}

@keyframes accountHomeTextFlow {
  from {
    background-position:
      0% 50%;
  }

  to {
    background-position:
      200% 50%;
  }
}

@keyframes accountHomeCardSweep {
  0%,
  18% {
    left: -50px;
    opacity: 0;
  }

  30% {
    opacity: 1;
  }

  52% {
    left: calc(100% + 50px);
    opacity: 0;
  }

  100% {
    left: calc(100% + 50px);
    opacity: 0;
  }
}

@keyframes accountHomeButtonSweep {
  0%,
  20% {
    left: -54px;
    opacity: 0;
  }

  32% {
    opacity: 1;
  }

  54% {
    left: calc(100% + 54px);
    opacity: 0;
  }

  100% {
    left: calc(100% + 54px);
    opacity: 0;
  }
}

@keyframes accountHomeModalSweep {
  0%,
  18% {
    left: -48px;
    opacity: 0;
  }

  30% {
    opacity: 0.85;
  }

  52% {
    left: calc(100% + 48px);
    opacity: 0;
  }

  100% {
    left: calc(100% + 48px);
    opacity: 0;
  }
}

/* =========================================================
 * 手机适配
 * ========================================================= */

@media (max-width: 390px) {
  .hero-capability-grid {
    gap: 6px;
  }

  .hero-capability-grid > div {
    min-height: 64px;
    padding-right: 5px;
    padding-left: 5px;
  }

  .hero-capability-grid small {
    font-size: 6px;
  }

  .hero-capability-grid strong {
    font-size: 9px;
  }

  .balance-card {
    grid-template-columns:
      minmax(0, 1fr)
      100px;
  }

  .balance-meta-item {
    min-width: 78px;
    padding-right: 10px;
  }
}

/* =========================================================
 * 桌面端
 * ========================================================= */

@media (min-width: 768px) {
  .hero-premium {
    min-height: 540px;
  }

  .hero-capability-grid {
    max-width: 650px;
    gap: 12px;
  }

  .hero-capability-grid > div {
    min-height: 80px;
    padding: 15px 13px;
  }

  .hero-capability-grid small {
    font-size: 8px;
  }

  .hero-capability-grid strong {
    font-size: 12px;
  }

  .balance-card {
    padding: 28px 30px;
  }

  .balance-meta {
    gap: 24px;
  }

  .ledger-card {
    padding: 26px;
  }
}

/* 用户启用减少动画时 */
@media (prefers-reduced-motion: reduce) {
  .account-grid-layer,
  .account-scan-beam,
  .account-aurora,
  .account-particles i,
  .hero-premium,
  .hero-premium::before,
  .hero-premium::after,
  .hero-grid-bg,
  .hero-glow-gold,
  .hero-glow-blue,
  .hero-scan-line,
  .hero-data-stream span,
  .hero-content,
  .hero-badge::after,
  .hero-badge i,
  .hero-title-accent,
  .balance-card::before,
  .balance-card::after,
  .balance-kicker i,
  .withdraw-button::before,
  .ledger-card::before,
  .withdraw-modal::before,
  .withdraw-modal::after,
  .modal-glow,
  .confirm-button::before {
    animation: none !important;
  }
}


</style>